import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useDemo } from '../contexts/DemoContext';

import { CollectionCard } from './CollectionCard';
import { ProductCard } from './ProductCard';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useGetProducts } from '@/hooks/useGetProducts';
import { Product } from '@/services/ProductsService';

const collections = [
  {
    id: 1,
    name: 'Novidades',
    description: 'Últimas tendências da temporada',
    productCount: 24,
  },
  {
    id: 2,
    name: 'Mais Vendidos',
    description: 'Os produtos mais populares',
    productCount: 32,
  },
  {
    id: 3,
    name: 'Inverno 2025',
    description: 'Coleção especial de inverno',
    productCount: 18,
  },
  {
    id: 4,
    name: 'Esporte',
    description: 'Performance e conforto',
    productCount: 28,
  },
];

export function HomePage() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Demo.components.HomePage',
  });

  const { user } = useAuth();

  const { setPage } = useDemo();

  const { products } = useGetProducts({ limit: 20 });

  return (
    <div className="min-h-screen bg-background">
      {/* Demo Badge */}
      <div className="border-b border-border bg-primary/5">
        <div className="mx-auto px-4 py-2 text-center">
          <Badge variant="outline" className="text-xs">
            Fashion Demo - {user?.customer.name}
          </Badge>
        </div>
      </div>

      {/* Hero Section */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl text-center">
          <h1 className="mb-4 text-balance text-4xl font-medium text-foreground md:text-5xl">
            {t('title')}
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            {t('description')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="gap-2"
              onClick={() => setPage('catalog')}
            >
              {t('catalogButton')}
              <ArrowRight className="h-4 w-4" />
            </Button>

            <Button asChild variant="outline" size="lg" className="hidden">
              {t('collectionButton')}
            </Button>
          </div>
        </div>
      </section>

      {/* Collections Section */}
      <section id="collections" className="hidden bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8">
            <h2 className="mb-2 text-2xl font-medium text-foreground md:text-3xl">
              {t('collectionHighlight')}
            </h2>
            <p className="text-muted-foreground">
              {t('collectionDescription')}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {collections.map((collection) => (
              <CollectionCard key={collection.id} collection={collection} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8">
            <h2 className="mb-2 text-2xl font-medium text-foreground md:text-3xl">
              {t('productHighlight')}
            </h2>

            <p className="text-muted-foreground">{t('catalogPreview')}</p>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {products
              .reduce((acc: Product[], product) => {
                if (!acc.find((p) => p.productId === product.productId)) {
                  acc.push(product);
                }
                return acc;
              }, [])
              .map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
